import type { CreditPack } from '@pulsify/shared/dto'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { creditsApi } from '~/entities/credits/api/credits'
import { creditPackReturnUrls } from '~/entities/credits/lib/credit-pack-return'
import { usePricing } from '~/entities/subscription/model/use-stripe'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'

/** The credits on sale, or null while they load or when Stripe offers none. */
export function useCreditPack(): CreditPack | null {
  const { data } = usePricing()
  return data?.creditPack ?? null
}

/**
 * Sends the buyer to Stripe Checkout for the credits they picked, which brings them back to the
 * page they are on.
 */
export function useBuyCreditPack(businessId: string) {
  return useMutation({
    mutationFn: (credits: number) =>
      creditsApi.createPackCheckout({
        businessId,
        credits,
        ...creditPackReturnUrls(window.location.href),
      }),
    onSuccess: (data) => {
      if (data.url) window.location.href = data.url
    },
    onError: (error) => {
      toast.error(getErrorMessageFromError(error))
    },
  })
}

/** Books a pack paid in Checkout without waiting for Stripe's webhook, then rereads the balance. */
export function useConfirmCreditPack() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (vars: { businessId: string; sessionId: string }) =>
      creditsApi.confirmPack(vars.businessId, vars.sessionId),
    onSettled: (_data, _error, vars) => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.CREDITS_BALANCE, vars.businessId),
      })
    },
  })
}
